import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { notification, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PageHeader from '../layout/PageHeader';
import WalletCreateModal from '../components/WalletCreateModal/WalletCreateModal';
import WalletsFilters from '../components/WalletsPage/WalletsFilters';
import WalletsNewList from '../components/WalletsPage/WalletsNewList';
import Wallets2IntroModal from '../components/WalletsPage/Wallets2IntroModal';
import WalletDeleteModal from '../components/WalletsPage/MerchantWalletsActions/WalletDeleteModal';
import WalletSettingsModal from '../components/WalletsPage/MerchantWalletsActions/WalletSettingsModal';
import SignPasswordModal from '../components/SignPasswordModal/SignPasswordModal';
import PasswordModal from '../components/WalletsPage/PasswordModal';
import { identity } from '../utils/getIdentity';
import { getMerchantData } from '../redux/actions/merchantData';
import {
  getMerchantWallets,
  getMoreMerchantWallets,
  deleteMerchantWallet,
  resetTwoFaState,
  setWalletAsMain,
  getWalletPrivateKey,
  getWalletMnemonicPhrase,
  getPassphrase,
  resetPageCounter,
  merchantWalletsReset,
} from '../redux/actions/merchantWallets';
import {
  checkAutosignStatus,
  checkPasswordStatus,
  downloadAutosign,
  downloadNewAutosignError,
  setPasswordSignature,
} from '../redux/actions/withdraw';

const WALLETS_NEW_HIDE_ZERO_STORAGE_KEY = 'cpayHideZeroBalancesWalletsNew';
const FORM_NAME = 'walletsNewFilters';

const WalletsNewPage = ({
  merchantId,
  userRole,
  networkFilter,
  merchantData,
  currencies,
  wallets,
  page,
  pages,
  countItem,
  fetching,
  walletsFilters,
  autosignStatus,
  passwordStatus,
  autosignStep,
  downloadAutosignStep,
  downloadError,
  getMerchantData,
  getMerchantWallets,
  getMoreMerchantWallets,
  deleteMerchantWallet,
  resetTwoFaState,
  setWalletAsMain,
  getWalletPrivateKey,
  getWalletMnemonicPhrase,
  getPassphrase,
  resetPageCounter,
  downloadAutosign,
  setPasswordSignature,
  checkAutosignStatus,
  checkPasswordStatus,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [showIntroModal, setShowIntroModal] = useState(false);
  const [isDisableSpinner, setIsDisableSpinner] = useState(false);
  const [hideZeroCheckbox, setHideZeroCheckbox] = useState(() => {
    try {
      const stored = localStorage.getItem(WALLETS_NEW_HIDE_ZERO_STORAGE_KEY);
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });
  const [walletVersionTick, setWalletVersionTick] = useState(0);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openSettingsModal, setOpenSettingsModal] = useState(false);
  const [isOpenSignPassword, setIsOpenSignPassword] = useState(false);
  const [isOpenDownload, setIsOpenDownload] = useState(false);
  const [modalTitle, setModalTitle] = useState(false);
  const [password, setPassword] = useState('');
  const [securityRefreshedWalletId, setSecurityRefreshedWalletId] = useState(null);
  const selectedWalletRef = useRef(null);
  selectedWalletRef.current = selectedWallet;

  const consumeSecurityRefresh = useCallback(() => {
    setSecurityRefreshedWalletId(null);
  }, []);

  useEffect(() => {
    dispatch(merchantWalletsReset());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (merchantId) {
      getMerchantData(merchantId);
    }
    // getMerchantData is intentionally omitted — stable dispatch-bound function
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);

  useEffect(() => {
    if (merchantData?.settingWallet?.defaultSetup === 'v1' && merchantData?.settingWallet?.version === 'v1') {
      setShowIntroModal(true);
      setIsDisableSpinner(true);
    }
  }, [merchantData?.settingWallet?.defaultSetup, merchantData?.settingWallet?.version]);

  useEffect(() => {
    const bump = () => setWalletVersionTick(n => n + 1);
    const onStorage = e => {
      if (e.key === 'walletVersion') bump();
    };
    window.addEventListener('walletVersionChange', bump);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('walletVersionChange', bump);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const currencyIds = useMemo(
    () => currencies?.filter(c => c.currencyType === 'currency')?.map(c => c._id) ?? [],
    [currencies]
  );

  const effectiveWalletVersion = merchantData?.settingWallet?.version || localStorage.getItem('walletVersion');
  
  const submitFilters = useCallback(() => {
    const { order, search, currencyIds: selectedIds } = walletsFilters || {};
    if (merchantId && networkFilter && currencyIds.length > 0) {
      resetPageCounter();
      getMerchantWallets(merchantId, {
        typeNetwork: networkFilter,
        walletVersion: 'v2',
        order: order || 'DESC',
        limit: 10,
        page: 1,
        currencyIds: (selectedIds?.length > 0 ? selectedIds : currencyIds).join(',') || undefined,
        typeWallet: 'merchant',
        showTokens: false,
        search: (search || '').trim() || undefined,
        positiveBalance: hideZeroCheckbox,
      });
    }
  }, [merchantId, networkFilter, currencyIds, walletsFilters, hideZeroCheckbox, resetPageCounter, getMerchantWallets]);

  useEffect(() => {
    if (
      walletsFilters &&
      typeof walletsFilters === 'object' &&
      merchantId &&
      merchantId.length &&
      currencyIds.length > 0
    ) {
      if (walletsFilters.limit || walletsFilters.order) {
        submitFilters();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, walletsFilters?.limit, walletsFilters?.order, networkFilter, hideZeroCheckbox, walletVersionTick]);

  const resetFilters = useCallback(() => {
    if (merchantId && networkFilter && currencyIds.length > 0) {
      resetPageCounter();
      getMerchantWallets(merchantId, {
        typeNetwork: networkFilter,
        walletVersion: 'v2',
        order: 'DESC',
        limit: 10,
        page: 1,
        currencyIds: currencyIds.join(',') || undefined,
        typeWallet: 'merchant',
        showTokens: false,
        positiveBalance: hideZeroCheckbox,
      });
    }
  }, [merchantId, networkFilter, currencyIds, hideZeroCheckbox, resetPageCounter, getMerchantWallets]);

  // wallet modals use this to re-fetch current page after delete/update
  const walletsFiltersForModals = useMemo(
    () => ({ currencyIds, limit: 10, order: walletsFilters?.order || 'DESC' }),
    [currencyIds, walletsFilters?.order]
  );

  const WalletCreateComponent = useCallback(() => (
    <WalletCreateModal
      merchantId={merchantId}
      typeWallet="merchant"
      filtersFormName={FORM_NAME}
      networkFilter={networkFilter}
    />
  ), [merchantId, networkFilter]);

  const openNotification = () => {
    notification.success({ message: 'Copied', duration: 2 });
  };

  const closeSign = () => {
    setIsOpenSignPassword(false);
    dispatch(downloadNewAutosignError(false));
    setModalTitle(false);
    const w = selectedWalletRef.current;
    if (merchantId && w?._id) {
      checkPasswordStatus(merchantId, w._id);
      setSecurityRefreshedWalletId(w._id);
    }
  };

  const handleOpenSignPassword = useCallback(
    wallet => {
      setSelectedWallet(wallet);
      checkAutosignStatus(merchantId, wallet?._id);
      checkPasswordStatus(merchantId, wallet?._id);
      setIsOpenSignPassword(true);
    },
    [merchantId]
  );

  const openDownload = () => {
    setOpenSettingsModal(false);
    setIsOpenDownload(true);
  };

  const checkDownloadAutosign = useCallback(() => {
    const w = selectedWalletRef.current;
    if (merchantId && w?._id) {
      checkAutosignStatus(merchantId, w._id);
      checkPasswordStatus(merchantId, w._id);
    }
  }, [merchantId]);

  const handleOpenSettings = wallet => {
    setSelectedWallet(wallet);
    setOpenSettingsModal(true);
    checkAutosignStatus(merchantId, wallet?._id);
    checkPasswordStatus(merchantId, wallet?._id);
  };

  const handleOpenDelete = wallet => {
    setSelectedWallet(wallet);
    setOpenDeleteModal(true);
  };

  const closeSettingsOnly = () => {
    setOpenSettingsModal(false);
  };

  const showEmptyState = !fetching && (!wallets || wallets.length === 0);
  const showList = !showIntroModal && wallets?.length > 0;

  return (
    <React.Fragment>
      <WalletDeleteModal
        openDeleteModal={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
        merchantId={merchantId}
        walletId={selectedWallet?._id}
        walletAddress={selectedWallet?.address}
        deleteMerchantWallet={deleteMerchantWallet}
        typeWallet="merchant"
        page={page}
        pages={pages}
        countItem={countItem}
        walletsFilters={walletsFiltersForModals}
        networkFilter={networkFilter}
        isFromMerchantsWallets={true}
      />
      <SignPasswordModal
        merchantId={merchantId}
        autosignStatus={autosignStatus}
        passwordStatus={passwordStatus}
        isOpen={isOpenSignPassword}
        walletId={selectedWallet?._id}
        downloadAutosign={downloadAutosign}
        setPasswordSignature={setPasswordSignature}
        onCancel={closeSign}
        modalTitle={modalTitle}
        autosignStep={autosignStep}
        downloadAutosignStep={downloadAutosignStep}
        password={password}
      />
      <WalletSettingsModal
        openSettingsModal={openSettingsModal}
        setOpenSettingsModal={setOpenSettingsModal}
        selectedWallet={selectedWallet}
        wallet={selectedWallet}
        merchantId={merchantId}
        setSelectedWallet={setSelectedWallet}
        getWalletPrivateKey={getWalletPrivateKey}
        getWalletMnemonicPhrase={getWalletMnemonicPhrase}
        getPassphrase={getPassphrase}
        openNotification={openNotification}
        resetTwoFaState={resetTwoFaState}
        setWalletAsMain={setWalletAsMain}
        closeModal={closeSettingsOnly}
        statusAutosign={autosignStatus}
        statusPassword={passwordStatus}
        openSign={() => setIsOpenSignPassword(true)}
        typeWallet="merchant"
        downloadAutosign={downloadAutosign}
        checkStatusPassword={() => {
          const w = selectedWalletRef.current;
          if (merchantId && w?._id) {
            checkPasswordStatus(merchantId, w._id);
          }
        }}
        page={page}
        walletsFilters={walletsFiltersForModals}
        networkFilter={networkFilter}
        openDownload={openDownload}
        updateList={() => {
          checkDownloadAutosign();
          submitFilters();
          const w = selectedWalletRef.current;
          if (w?._id) {
            setSecurityRefreshedWalletId(w._id);
          }
        }}
        downloadError={downloadError}
        setIsSegwit={() => submitFilters()}
      />
      <PasswordModal
        isOpen={isOpenDownload}
        closeModal={() => setIsOpenDownload(false)}
        downloadAutosignStep={downloadAutosignStep}
        merchantId={merchantId}
        walletId={selectedWallet?._id}
        downloadAutosign={downloadAutosign}
      />
      <Wallets2IntroModal
        visible={showIntroModal}
        onClose={() => {
          setShowIntroModal(false);
          setIsDisableSpinner(false);
        }}
        merchantId={merchantId}
        networkFilter={networkFilter}
        currencyIds={currencyIds}
        getMerchantWallets={() => {
          setIsDisableSpinner(false);
          getMerchantWallets(merchantId, { typeNetwork: networkFilter });
        }}
        getMerchantData={getMerchantData}
      />
      <PageHeader showBackButton={userRole.includes('corporate')} CreateModalComponent={WalletCreateComponent} />
      <div className="block-for-component">
        <div className={`component component-${identity}`}>
          <WalletsFilters
            formName={FORM_NAME}
            merchantName={merchantData?.name || ''}
            currencies={currencies}
            initialValues={{
              search: '',
              order: 'DESC',
              limit: 10,
              currencyIds: currencyIds,
            }}
            submitFilters={submitFilters}
            resetFilters={resetFilters}
            fetching={fetching}
            hideZeroCheckbox={hideZeroCheckbox}
            setCheckbox={value => {
              setHideZeroCheckbox(value);
              localStorage.setItem(WALLETS_NEW_HIDE_ZERO_STORAGE_KEY, JSON.stringify(value));
            }}
            typeWallets="merchant"
          />
          {!isDisableSpinner && fetching && !wallets?.length && (
            <div style={{ minHeight: 120, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
            </div>
          )}
          {showList && (
            <WalletsNewList
              wallets={wallets}
              page={page}
              pages={pages}
              fetching={fetching}
              merchantId={merchantId}
              networkFilter={networkFilter}
              currencyIds={currencyIds}
              getMerchantWallets={getMerchantWallets}
              getMoreMerchantWallets={getMoreMerchantWallets}
              search={walletsFilters?.search || ''}
              hideZeroCheckbox={hideZeroCheckbox}
              walletVersion={effectiveWalletVersion}
              openSettings={handleOpenSettings}
              openDelete={handleOpenDelete}
              openSignPassword={handleOpenSignPassword}
              securityRefreshedWalletId={securityRefreshedWalletId}
              onSecurityRefreshConsumed={consumeSecurityRefresh}
            />
          )}
          {showEmptyState && (
            <div className="wallets-new-empty">
              <p className="wallets-new-empty__text">{t('nodata')}</p>
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = state => ({
  merchantId: state.transactions.merchantId,
  userRole: state.user.data.roles,
  networkFilter: state.networkFilter,
  merchantData: state.merchantData.data,
  currencies: state.currencies.data,
  wallets: state.merchantWallets.data,
  page: state.merchantWallets.page,
  pages: state.merchantWallets.pages,
  countItem: state.merchantWallets.countItem,
  fetching: state.merchantWallets.fetching,
  walletsFilters: formValueSelector(FORM_NAME)(state, 'search', 'order', 'limit', 'currencyIds'),
  autosignStatus: state.withdraw.autosignStatus,
  passwordStatus: state.withdraw.passwordStatus,
  autosignStep: state.withdraw.autosignStep,
  downloadAutosignStep: state.withdraw.downloadAutosignStep,
  downloadError: state.withdraw.downloadError,
});

const mapDispatchToProps = dispatch => ({
  getMerchantData: getMerchantData(dispatch),
  getMerchantWallets: getMerchantWallets(dispatch),
  getMoreMerchantWallets: getMoreMerchantWallets(dispatch),
  deleteMerchantWallet: deleteMerchantWallet(dispatch),
  resetTwoFaState: resetTwoFaState(dispatch),
  setWalletAsMain: setWalletAsMain(dispatch),
  getWalletPrivateKey: getWalletPrivateKey(dispatch),
  getWalletMnemonicPhrase: getWalletMnemonicPhrase(dispatch),
  getPassphrase: getPassphrase(dispatch),
  resetPageCounter,
  downloadAutosign: downloadAutosign(dispatch),
  setPasswordSignature: setPasswordSignature(dispatch),
  checkAutosignStatus: checkAutosignStatus(dispatch),
  checkPasswordStatus: checkPasswordStatus(dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(WalletsNewPage);
